/**
 * Reusable payment terms calculation utility
 * Calculates payment schedule based on event date and booking amount
 */

/**
 * Calculate payment terms based on event date
 * @param {string|Date} eventDate - The event date
 * @param {number} subtotal - The subtotal amount (before platform fee)
 * @returns {Object|null} Payment terms object or null if invalid date
 */
export const calculatePaymentTerms = (eventDate, subtotal = 0) => {
  if (!eventDate) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventDateObj = new Date(eventDate);
  eventDateObj.setHours(0, 0, 0, 0);
  
  const diffTime = eventDateObj - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffDays < 0) {
    return null; // Past date
  }

  // 2-3 weeks away: 100% payment
  if (diffWeeks >= 2 && diffWeeks <= 3) {
    return {
      depositPercentage: 100,
      depositLabel: '100%',
      schedule: [
        { label: 'Full Payment', percentage: 100, dueDate: 'Due now to book date' }
      ],
      description: '100% payment should be made to book date if event is 2-3 weeks away.'
    };
  }

  // 1-5 months away: 70% deposit, 30% 2 weeks before
  if (diffMonths >= 1 && diffMonths <= 5) {
    const twoWeeksBefore = new Date(eventDateObj);
    twoWeeksBefore.setDate(twoWeeksBefore.getDate() - 14);
    
    return {
      depositPercentage: 70,
      depositLabel: '70%',
      schedule: [
        { label: 'Deposit', percentage: 70, dueDate: 'Due now to book date' },
        { label: 'Final Payment', percentage: 30, dueDate: `Due by ${twoWeeksBefore.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (2 weeks before event)` }
      ],
      description: '70% of the package price should be paid as deposit for the date to be booked if the event is 1-5 months away from the day of booking. The remaining 30% should be paid in not less than 2 weeks to engagement or wedding.'
    };
  }

  // 6-9 months away: 50% deposit, 50% 2 weeks before
  if (diffMonths >= 6 && diffMonths <= 9) {
    const twoWeeksBefore = new Date(eventDateObj);
    twoWeeksBefore.setDate(twoWeeksBefore.getDate() - 14);
    
    return {
      depositPercentage: 50,
      depositLabel: '50%',
      schedule: [
        { label: 'Deposit', percentage: 50, dueDate: 'Due now to book date' },
        { label: 'Final Payment', percentage: 50, dueDate: `Due by ${twoWeeksBefore.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (2 weeks before event)` }
      ],
      description: '50% of the package price should be paid as deposit for the date to be booked if the event is 6-9 months away from the day of booking. The remaining balance should be paid in not less than 2 weeks to the event.'
    };
  }

  // 10-24 months away: 30% deposit, 70% total by 2 months before, 30% final 2 weeks before
  if (diffMonths >= 10 && diffMonths <= 24) {
    const twoMonthsBefore = new Date(eventDateObj);
    twoMonthsBefore.setMonth(twoMonthsBefore.getMonth() - 2);
    const twoWeeksBefore = new Date(eventDateObj);
    twoWeeksBefore.setDate(twoWeeksBefore.getDate() - 14);
    
    return {
      depositPercentage: 30,
      depositLabel: '30%',
      schedule: [
        { label: 'Initial Deposit', percentage: 30, dueDate: 'Due now to book date' },
        { label: 'Second Payment', percentage: 40, dueDate: `Due by ${twoMonthsBefore.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (2 months before event - total should be 70%)` },
        { label: 'Final Payment', percentage: 30, dueDate: `Due by ${twoWeeksBefore.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (2 weeks before event)` }
      ],
      description: '30% of the package price in advance to be paid as deposit to book the dates if the event is 10-24 months away from the day of booking. Payment should be updated to 70% by 2 months to the event. The remaining 30% should be paid in not less than 2 weeks to engagement or wedding.'
    };
  }

  // More than 24 months: default to 30% (same as 10-24 months)
  if (diffMonths > 24) {
    const twoMonthsBefore = new Date(eventDateObj);
    twoMonthsBefore.setMonth(twoMonthsBefore.getMonth() - 2);
    const twoWeeksBefore = new Date(eventDateObj);
    twoWeeksBefore.setDate(twoWeeksBefore.getDate() - 14);
    
    return {
      depositPercentage: 30,
      depositLabel: '30%',
      schedule: [
        { label: 'Initial Deposit', percentage: 30, dueDate: 'Due now to book date' },
        { label: 'Second Payment', percentage: 40, dueDate: `Due by ${twoMonthsBefore.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (2 months before event)` },
        { label: 'Final Payment', percentage: 30, dueDate: `Due by ${twoWeeksBefore.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (2 weeks before event)` }
      ],
      description: '30% of the package price in advance to be paid as deposit to book the dates if the event is 10-24 months away from the day of booking. Payment should be updated to 70% by 2 months to the event. The remaining 30% should be paid in not less than 2 weeks to engagement or wedding.'
    };
  }

  // Less than 2 weeks: 100% payment
  return {
    depositPercentage: 100,
    depositLabel: '100%',
    schedule: [
      { label: 'Full Payment', percentage: 100, dueDate: 'Due now to book date' }
    ],
    description: '100% payment should be made to book date if event is less than 2 weeks away.'
  };
};

/**
 * Calculate payment amount for a specific payment in the schedule
 * @param {number} subtotal - The subtotal amount
 * @param {number} platformFee - The total platform fee
 * @param {number} percentage - The percentage of this payment
 * @returns {number} The payment amount including proportional platform fee
 */
export const calculatePaymentAmount = (subtotal, platformFee, percentage) => {
  const subtotalAmount = subtotal * (percentage / 100);
  const proportionalFee = platformFee * (percentage / 100);
  return Math.round((subtotalAmount + proportionalFee) * 100) / 100;
};

/**
 * Calculate remaining payments based on what's already paid
 * @param {Object} paymentTerms - Payment terms object
 * @param {Array} completedPayments - Array of completed payment objects
 * @param {number} subtotal - The subtotal amount
 * @param {number} platformFee - The total platform fee
 * @returns {Array} Array of remaining payment options
 */
export const calculateRemainingPayments = (paymentTerms, completedPayments, subtotal, platformFee) => {
  if (!paymentTerms || !paymentTerms.schedule) {
    return [];
  }

  // Calculate total amount paid
  const totalPaid = completedPayments.reduce((sum, payment) => {
    return sum + parseFloat(payment.amount || 0);
  }, 0);

  const totalDue = subtotal + platformFee;
  
  // Calculate cumulative percentages paid
  const completedPercentages = totalDue > 0 ? (totalPaid / totalDue) * 100 : 0;

  // Filter out payments that have been completed
  const remainingPayments = paymentTerms.schedule
    .map((payment, index) => {
      // Calculate cumulative percentage up to this payment
      let cumulativePercentage = 0;
      for (let i = 0; i <= index; i++) {
        cumulativePercentage += paymentTerms.schedule[i].percentage;
      }

      // Calculate cumulative percentage up to previous payment
      let previousCumulativePercentage = 0;
      for (let i = 0; i < index; i++) {
        previousCumulativePercentage += paymentTerms.schedule[i].percentage;
      }

      // Check if this payment (or earlier payments) have been made
      const isPaid = cumulativePercentage <= completedPercentages;
      const isPartiallyPaid = completedPercentages > previousCumulativePercentage && completedPercentages < cumulativePercentage;

      // Calculate payment amount
      const paymentAmount = calculatePaymentAmount(subtotal, platformFee, payment.percentage);
      
      // For partial payments, calculate remaining amount
      let remainingAmount = paymentAmount;
      if (isPartiallyPaid) {
        const remainingPercentage = cumulativePercentage - completedPercentages;
        remainingAmount = calculatePaymentAmount(subtotal, platformFee, remainingPercentage);
      }

      return {
        ...payment,
        index,
        cumulativePercentage,
        previousCumulativePercentage,
        isPaid,
        isPartiallyPaid,
        amount: paymentAmount,
        remainingAmount: remainingAmount
      };
    })
    .filter(payment => !payment.isPaid); // Only show unpaid payments

  return remainingPayments;
};

/**
 * Get the next payment option based on payment schedule
 * @param {Object} paymentTerms - Payment terms object
 * @param {Array} completedPayments - Array of completed payment objects
 * @param {number} subtotal - The subtotal amount
 * @param {number} platformFee - The total platform fee
 * @returns {Object|null} Next payment option or null
 */
export const getNextPaymentOption = (paymentTerms, completedPayments, subtotal, platformFee) => {
  const remainingPayments = calculateRemainingPayments(paymentTerms, completedPayments, subtotal, platformFee);
  return remainingPayments.length > 0 ? remainingPayments[0] : null;
};

